import { Dispatch, SetStateAction, useEffect } from 'react';
import { useLazyLoadQuery } from 'react-relay';
import { UsersByRoleClassesRosterQuery } from '../../graphQLOperations/__generated__/UsersByRoleClassesRosterQuery.graphql';
import { usersByRoleClassesRoster } from '../../graphQLOperations/Users';
import { TeacherClasses } from '../../models/TeacherClasses';
import { StudentsTableFilterState } from './StudentsDataTable';
import { CompleteUser } from '../../custom-hooks/useGetUsersBySite';
import { sortOrderCodeForQueries } from '../../businessLogicService/sortOrderCodeForQueries';
import { NO_ASSIGNED_ROADMAP } from '../ClassesRoster/ClassesRosterRightSide';

export interface QueryUsersDataProp {
  filterState: StudentsTableFilterState;
  setFetchingData: Dispatch<SetStateAction<boolean>>;
  setStudentsFiltered?: Dispatch<SetStateAction<CompleteUser[]>>;
  setTotalRecords: Dispatch<SetStateAction<number>>;
  setPageInfo: Dispatch<
    SetStateAction<
      | {
          endCursor: string | null | undefined;
          hasNextPage: boolean;
          hasPreviousPage: boolean;
          startCursor: string | null | undefined;
        }
      | undefined
    >
  >;
  pageInfo:
    | {
        endCursor: string | null | undefined;
        hasNextPage: boolean;
        hasPreviousPage: boolean;
        startCursor: string | null | undefined;
      }
    | undefined;
  fetchingData: boolean;
  firstLoad: boolean;
  setFirstLoad: Dispatch<SetStateAction<boolean>>;
  setTeachersFiltered?: Dispatch<SetStateAction<TeacherClasses[]>>;
  studentsPagination?: Record<number, { endCursor?: string | null; startCursor?: string | null }>;
  setStudentsPagination?: Dispatch<
    SetStateAction<Record<number, { endCursor?: string | null; startCursor?: string | null }>>
  >;
}

export const QueryStudentsData = ({
  setFetchingData,
  setStudentsFiltered,
  filterState,
  setTotalRecords,
  setPageInfo,
  pageInfo,
  fetchingData,
  firstLoad,
  setFirstLoad,
  setStudentsPagination,
  studentsPagination,
}: QueryUsersDataProp) => {
  const isMovingNext = filterState.page! - filterState.prevPage === 1;
  const isMovingPrevious = filterState.page! - filterState.prevPage === -1;
  const isNotMoving = filterState.page! - filterState.prevPage === 0;
  const notAssignedRoadmapInclude = filterState.filters.roadmaps?.some(item => item.id === NO_ASSIGNED_ROADMAP);
  const cleanRoadmaps = filterState.filters.roadmaps?.filter(item => item.id !== NO_ASSIGNED_ROADMAP);
  const currentPage = filterState.page ?? 0;
  const afterCursorForPage = currentPage > 0 ? studentsPagination?.[currentPage - 1]?.endCursor : undefined;
  const shouldUseAfter = fetchingData && (isMovingNext || (isNotMoving && currentPage > 0));
  const usersData = useLazyLoadQuery<UsersByRoleClassesRosterQuery>(
    usersByRoleClassesRoster,
    {
      filter: {
        userEmailContains: filterState.filters.emailAddress,
        userEmails: null,
        isActive: 'ACTIVE',
        siteIds: filterState.filters.sites?.map(st => st.id),
        classIds: filterState.filters.classes?.length ? filterState.filters.classes.map(cl => cl.id) : null,
        roleIds: ['RoleNode:2'],
        gradeLevelIds: filterState.filters.grades?.map(grade => grade.id),
        userNameContains: filterState.filters.name,
        userWithoutTargetGroups: notAssignedRoadmapInclude,
        currentTargetGroupIds: cleanRoadmaps?.length ? cleanRoadmaps.map(rm => rm.id) : null,
      },
      first: fetchingData && (isMovingNext || isNotMoving || !pageInfo?.hasPreviousPage) ? filterState.rows : null,
      last: fetchingData && isMovingPrevious && pageInfo?.hasPreviousPage ? filterState.rows : null,
      order: [{ lastName: sortOrderCodeForQueries(filterState.sortOrder || 1) }],
      skip: !firstLoad && !fetchingData,
      after: shouldUseAfter ? afterCursorForPage : undefined,
      before: fetchingData && isMovingPrevious && pageInfo?.hasPreviousPage ? pageInfo?.startCursor : undefined,
    },
    { fetchPolicy: 'network-only', fetchKey: JSON.stringify(filterState) },
  );

  useEffect(() => {
    if (usersData?.allUsers) {
      setTotalRecords(usersData?.allUsers?.totalCount ?? 0);
      setPageInfo(usersData?.allUsers?.pageInfo);
      setStudentsPagination?.(prev => ({
        ...prev,
        [filterState.page ?? 0]: {
          startCursor: usersData?.allUsers?.pageInfo?.startCursor,
          endCursor: usersData?.allUsers?.pageInfo?.endCursor,
        },
      }));
      if (setStudentsFiltered) {
        setStudentsFiltered(
          usersData?.allUsers?.nodes?.map(
            student =>
              ({
                emailAddress: student.email,
                site: student.sites,
                id: student?.id ?? '',
                publicUserId: student?.publicUserId ?? '',
                name: `${student?.firstName || ''} ${student?.lastName || ''}`,
                firstName: student?.firstName ?? '',
                lastName: student?.lastName ?? '',
                grade: student.gradeLevels?.length ? student.gradeLevels[0] : { id: '', name: '' },
                activeUserClassesLength: student.userClasses?.filter(uc => uc?.isActive).length,
                classes:
                  student?.userClasses
                    ?.filter(uc => uc?.isActive)
                    .map(uc => (uc?.class?.section ? `${uc?.class.name} - ${uc.class.section}` : uc?.class?.name))
                    ?.join(', ') ?? '',
                externalSync: !!student.externalSync?.source,
                currentRoadmap: student.studentTargetGroups?.find(stg => stg?.isCurrent)?.targetGroup,
              }) as unknown as CompleteUser,
          ) ?? [],
        );
      }
      setFetchingData(false);
      setFirstLoad(false);
    }
    setFetchingData(false);
  }, [usersData]);

  return <div style={{ display: 'none' }}></div>;
};
